/* jshint esversion: 6 */
/**
 * Manejo de notificaciones push para Capacitor
 *
 * IMPORTANTE: Las notificaciones push se comportan diferente según el estado de la app:
 *
 * 1. FOREGROUND (app activa):
 *    - pushNotificationReceived: NO se dispara (comportamiento de Capacitor)
 *    - pushNotificationActionPerformed: Se dispara cuando llega la notificación
 *    - Las notificaciones se procesan a través de pushNotificationActionPerformed
 *
 * 2. BACKGROUND (app en segundo plano):
 *    - pushNotificationReceived: Se dispara y la notificación se muestra visualmente
 *    - pushNotificationActionPerformed: Se dispara cuando el usuario toca la notificación
 *    - Las notificaciones se procesan a través de pushNotificationReceived
 *
 * Por eso usamos pushNotificationReceived para background y pushNotificationActionPerformed
 * para foreground, asegurando que se procesen en ambos estados.
 */
import { PushNotifications } from '@capacitor/push-notifications';
import {
    ANDROID_NOTIFICATION_CHANNEL_CANCEL,
    ANDROID_NOTIFICATION_CHANNEL_DEFAULT,
    ANDROID_NOTIFICATION_CHANNEL_MESSAGES,
    ANDROID_NOTIFICATION_CHANNEL_SINGPS,
    ANDROID_NOTIFICATION_CHANNEL_URGENT,
    HOLATAXI_SOUNDS,
    getSoundExtension,
    normalizePushNotification
} from '@/config/notificationSound';

import store from '../store';
import * as types from '../store/mutation-types';
import bus from '../services/bus-event';

const TRACE_PREFIX = '[FCM_TRACE_APP]';
const RECENT_SOUND_WINDOW_MS = 1500;
const recentlyPlayedSoundByKey = new Map();

function buildSoundKey (notification) {
    if (!notification) return null;
    const data = notification.additionalData || notification.data || {};
    const messageId = notification.messageId || notification.id || data.messageId || null;
    const url = notification.url || data.url || null;
    const viajeId = notification.viaje_id || data.viaje_id || null;
    return [messageId || 'no-id', url || 'no-url', viajeId || 'no-trip'].join('|');
}

function shouldPlayNotificationSound (notification) {
    const now = Date.now();
    const key = buildSoundKey(notification);
    if (!key) return true;
    const last = recentlyPlayedSoundByKey.get(key);
    recentlyPlayedSoundByKey.set(key, now);
    recentlyPlayedSoundByKey.forEach((ts, k) => {
        if (now - ts > RECENT_SOUND_WINDOW_MS) {
            recentlyPlayedSoundByKey.delete(k);
        }
    });
    return !last || (now - last) > RECENT_SOUND_WINDOW_MS;
}

function buildTracePayload (notification) {
    if (!notification) return {};
    const data = notification.additionalData || notification.data || {};
    return {
        url: data.url || notification.url || null,
        viaje_id: data.viaje_id || null,
        messageId: notification.id || notification.messageId || null,
        title: notification.title || data.title || null,
        appForeground: store.getters['cordova/foreground']
    };
}

let acceptTrip = function (n) {
    if (!n || !n.additionalData) {
        console.error('acceptTrip: notification or additionalData is missing');
        return;
    }

    let currentPosition = store.getters['geolocation/actual'];
    let data = {
        lat: currentPosition.lat,
        lng: currentPosition.lng,
        viaje_id: parseInt(n.additionalData.viaje_id, 10),
        trip: n.additionalData.trip
    };
    console.log('acceptTrip', n, data);
    store.dispatch('driver/acceptTrip', data);
};

let rejectTrip = function (n) {
    if (!n || !n.additionalData) {
        console.error('rejectTrip: notification or additionalData is missing');
        return;
    }

    let currentPosition = store.getters['geolocation/actual'];
    let data = {
        lat: currentPosition.lat,
        lng: currentPosition.lng,
        viaje_id: parseInt(n.additionalData.viaje_id, 10),
        trip: n.additionalData.trip
    };
    console.log('ignoreTrip', data);
    store.dispatch('driver/ignoreTrip', data);
};

let openApp = function (n) {

};
let logoutApp = function (n) {
    store.dispatch('auth/logout', {});
};
let unavailableApp = function (n) {
    store.dispatch('driver/changeAvailability', false);
};

/**
 * Android 8+: el sonido del push en background depende del canal, no del campo sound del payload.
 * iOS: el sonido va en aps.sound (myride_notification.caf) + archivo en el bundle; no hay canales.
 */
function setupAndroidNotificationChannels () {
    const platform = window.device && window.device.platform;
    if (platform !== 'android') {
        return Promise.resolve();
    }

    const channelBase = {
        importance: 5,
        visibility: 1,
        vibration: true,
        lights: true
    };

    const channels = [
        {
            id: ANDROID_NOTIFICATION_CHANNEL_DEFAULT,
            name: 'Notificaciones',
            description: 'Canal por defecto',
            sound: `${HOLATAXI_SOUNDS.GENERICO}.${getSoundExtension(HOLATAXI_SOUNDS.GENERICO)}`,
            ...channelBase
        },
        {
            id: ANDROID_NOTIFICATION_CHANNEL_URGENT,
            name: 'Viajes y reservas',
            description: 'Nuevo viaje o reserva próxima',
            sound: `${HOLATAXI_SOUNDS.VIAJES}.wav`,
            ...channelBase
        },
        {
            id: ANDROID_NOTIFICATION_CHANNEL_MESSAGES,
            name: 'Mensajes',
            description: 'Mensajes y audios de chat',
            sound: `${HOLATAXI_SOUNDS.MENSAJES}.wav`,
            ...channelBase
        },
        {
            id: ANDROID_NOTIFICATION_CHANNEL_CANCEL,
            name: 'Cancelaciones',
            description: 'Viaje o reserva cancelada',
            sound: `${HOLATAXI_SOUNDS.CANCELAR}.wav`,
            ...channelBase
        },
        {
            id: ANDROID_NOTIFICATION_CHANNEL_SINGPS,
            name: 'GPS perdido',
            description: 'Sin señal GPS con sesión activa',
            sound: `${HOLATAXI_SOUNDS.SINGPS}.wav`,
            ...channelBase
        }
    ];

    return channels.reduce((chain, channel) => {
        return chain
            .then(() => PushNotifications.deleteChannel({ id: channel.id }).catch(() => {}))
            .then(() => PushNotifications.createChannel(channel));
    }, Promise.resolve())
        .then(() => console.log('Android notification channels ready'))
        .catch((err) => console.warn('Android notification channels setup failed', err));
}

function _init (force) {
    // Solicitar permiso de notificaciones
    console.log('_init');
    // Registrar listeners SIEMPRE, antes de pedir permisos

    // pushNotificationReceived: se dispara en foreground y background cuando usamos data-only + reenvío desde MyFirebaseMessagingService
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('=== PUSH NOTIFICATION RECEIVED ===');
        console.log('Notification received:', notification);

        // Mensajes data-only: title/body vienen en notification.data, no en la raíz
        notification.title = notification.title || (notification.data && notification.data.title);
        notification.body = notification.body || (notification.data && notification.data.body);
        notification.content = notification.body || notification.content;
        normalizePushNotification(notification);

        if (notification.url) {
            console.log('Processing notification with URL:', notification.url);
            console.log(TRACE_PREFIX, 'pushNotificationReceived', buildTracePayload(notification));
            store.dispatch('cordova/notificationArrive', notification);
        } else {
            console.log('Notification does not have URL, skipping processing');
            console.log(TRACE_PREFIX, 'pushNotificationReceived_missing_url', buildTracePayload(notification));
        }

        const isForeground = store.getters['cordova/foreground'];
        // En foreground forzamos sonido in-app para no perder alertas visualmente.
        // En background dejamos que lo maneje el sistema (APNS/canales Android).
        if (isForeground && shouldPlayNotificationSound(notification)) {
            import('@/services/Audio').then(({ playNotificationSound }) => {
                playNotificationSound(notification.sound, notification.url);
            });
        }
    });

    // Listener adicional para debugging - se debería disparar siempre
    PushNotifications.addListener('registration', (token) => {
        console.log('=== PUSH REGISTRATION SUCCESS ===');
        console.log('Token:', token.value);
        console.log('Push listeners should be active now');
        const tokenMasked = token && token.value ? `${token.value.substring(0, 6)}...${token.value.substring(token.value.length - 4)}` : null;
        console.log(TRACE_PREFIX, 'registration_success', { tokenMasked });
    });

    PushNotifications.addListener('registrationError', (error) => {
        console.log('=== PUSH REGISTRATION ERROR ===');
        console.log('Error:', error);
        console.log(TRACE_PREFIX, 'registration_error', { error });
    });

    // pushNotificationActionPerformed: Se dispara en FOREGROUND y BACKGROUND
    // NOTA: En foreground, este listener se dispara inmediatamente cuando llega la notificación
    // En background, se dispara cuando el usuario toca la notificación
    PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
        console.log('=== PUSH ACTION PERFORMED ===');
        console.log('Action performed:', notification.actionId, notification);
        const isForeground = store.getters['cordova/foreground'];
        console.log('App foreground state during action:', isForeground);

        if (isForeground) {
            import('@/services/Audio').then(({ playNotificationSound }) => {
                const n = normalizePushNotification(notification.notification || {});
                if (shouldPlayNotificationSound(n)) {
                    playNotificationSound(n.sound, n.url);
                }
            });
        }

        // IMPORTANTE: En foreground, procesar la notificación inmediatamente
        // En background, ya se procesó en pushNotificationReceived, pero lo procesamos de nuevo
        // por si acaso (no hay problema en procesar dos veces)
        if (notification.notification) {
            const n = normalizePushNotification(notification.notification);
            if (n.url) {
                if (isForeground) {
                    console.log('Processing FOREGROUND notification with URL:', n.url);
                } else {
                    console.log('Processing BACKGROUND notification again with URL:', n.url);
                }
                store.dispatch('cordova/notificationArrive', n);
                console.log(TRACE_PREFIX, 'pushNotificationActionPerformed', {
                    actionId: notification.actionId,
                    ...buildTracePayload(n)
                });
            }
        }

        // Manejar acciones específicas
        switch (notification.actionId) {
            case 'acceptTrip':
                acceptTrip(notification.notification);
                break;
            case 'rejectTrip':
                rejectTrip(notification.notification);
                break;
            case 'openApp':
                openApp(notification.notification);
                break;
            case 'logoutApp':
                logoutApp(notification.notification);
                break;
            case 'unavailableApp':
                unavailableApp(notification.notification);
                break;
            default:
                console.log('Unknown action:', notification.actionId);
                console.log(TRACE_PREFIX, 'unknown_action', { actionId: notification.actionId });
                break;
        }
    });

    // Listener para broadcast directo desde Firebase cuando la app está en foreground
    // Este es el método que usamos cuando Firebase detecta que estamos en foreground
    if (window.capacitor && window.capacitor.Plugins && window.capacitor.Plugins.App) {
        // Escuchar eventos de la app
        window.capacitor.Plugins.App.addListener('appStateChange', (state) => {
            console.log('=== APP STATE CHANGE ===', state);
        });
    }

    // Listener para broadcast nativo de Android
    if (window.capacitor && window.capacitor.Plugins && window.capacitor.Plugins.App) {
        // Intentar escuchar el broadcast personalizado
        try {
            // Crear un listener personalizado para el broadcast de Firebase
            const handleFirebaseBroadcast = (event) => {
                console.log('=== FIREBASE BROADCAST RECEIVED ===', event);

                if (event.detail && event.detail.data) {
                    // Crear una notificación simulada para Capacitor
                    const simulatedNotification = {
                        additionalData: event.detail.data,
                        messageId: event.detail.messageId,
                        title: event.detail.title,
                        body: event.detail.body
                    };

                    console.log('Simulated notification for Capacitor:', simulatedNotification);

                    // Procesar la notificación a través del flujo normal
                    if (simulatedNotification.additionalData && simulatedNotification.additionalData.url) {
                        console.log('Processing Firebase broadcast notification with URL:', simulatedNotification.additionalData.url);
                        console.log(TRACE_PREFIX, 'firebase_broadcast', buildTracePayload(simulatedNotification));
                        store.dispatch('cordova/notificationArrive', simulatedNotification);
                    }
                }
            };

            // Escuchar el evento personalizado
            document.addEventListener('firebase-notification', handleFirebaseBroadcast);
            console.log('Firebase broadcast listener registered');
        } catch (error) {
            console.warn('Could not register Firebase broadcast listener:', error);
        }
    }

    setupAndroidNotificationChannels().then(() => {
        return PushNotifications.requestPermissions();
    }).then(result => {
        console.log('requestPermissions', result);
        console.log(TRACE_PREFIX, 'permissions_result', result);
        if (result.receive === 'granted') {
            PushNotifications.register();
        } else {
            console.log('Permisos denegados para las notificaciones push');
        }
    }).catch((err) => {
        console.log('requestPermissions failed', err);
    });

    // Manejar el registro exitoso
    PushNotifications.addListener('registration', (token) => {
        console.log('Device registered successfully:', token.value);
        const tokenMasked = token && token.value ? `${token.value.substring(0, 6)}...${token.value.substring(token.value.length - 4)}` : null;
        console.log(TRACE_PREFIX, 'device_registered', { tokenMasked, forceUpdate: !!force });

        // Ejemplo de guardado del token en el store (Vuex)
        if (force) {
            store.commit('cordova/' + types.CORDOVA_DEVICE_UPDATED, token.value);
            bus.emit('device-updated');
        } else {
            store.commit('cordova/' + types.CORDOVA_DEVICE_REGISTER, token.value);
            bus.emit('device-register');
        }
        bus.emit('device-register');
    });

    // Manejar errores de registro
    PushNotifications.addListener('registrationError', (error) => {
        console.log('Registration error:', error);
        bus.emit('device-register');
    });
}

function _refresh () {
    console.log('refresh push');
    // if (push) {
        // push.unregister(function (param) {
        //     console.log('succesfully called unregister', param);
        //     _init(true);
        // }, function (param) {
        //     console.log('error when called unregister', param);
        //     _init(true);
        // });
    // }
}

export default {
    init () {
        console.log('init push.js', window.Pushy);
        _init(false);
    },
    refresh () {
        _refresh();
    }
};



// WEBPACK FOOTER //
// ./src/cordova/push.js